'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { BaseLineSwap } from '@/app/components/icons/BaseLineSwap';
import { useTeamSpaces } from '@/app/hooks';
import type { TeamSpaceInfo } from '@/app/types';
import { cn, PERSONAL_TEAM_SPACE_ID } from '@/app/utils';
import { getSpaceAvatarColor, getSpaceInitials } from '../utils';

const TeamAvatar = ({ spaceId, teamName, className }: { spaceId: string; teamName?: string; className?: string }) => {
  if (spaceId === PERSONAL_TEAM_SPACE_ID) {
    return <BaseLineSwap className={cn('size-4', className)} />;
  }

  return (
    <div
      className={cn('flex items-center justify-center', className)}
      style={{ backgroundColor: getSpaceAvatarColor(spaceId) }}
    >
      {getSpaceInitials(teamName)}
    </div>
  );
};

export const TeamSpaceSwitcher = () => {
  const t = useTranslations('teamProfile');
  const [open, setOpen] = useState(false);
  const { teamSpaces, currentSpaceId, currentSpace, switchToSpace } = useTeamSpaces();

  if (teamSpaces.length === 0) {
    return null;
  }

  const handleSpaceSwitch = (space: TeamSpaceInfo) => {
    switchToSpace(space.space_id);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-6 cursor-pointer items-center gap-1 rounded-8 border border-system-white08 bg-system-bg02 py-1 pr-1 pl-2 transition-colors hover:border-system-white12 hover:bg-system-bg03"
          >
            <span className="max-w-[143px] truncate font-medium text-white text-xs leading-6">
              {currentSpace.space_id === PERSONAL_TEAM_SPACE_ID
                ? t('personalWorkspace')
                : t('workspaceWithName', { name: currentSpace.team_name || '' })}
            </span>
            <BaseLineRight className="size-4 shrink-0 rotate-90 text-white transition-transform" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          sideOffset={4}
          align="center"
          className="w-[328px] select-none rounded-12 border border-system-white12 bg-[#2C2E33] p-2 shadow-[0px_8px_24px_0px_rgba(0,0,0,0.32)]"
        >
          <div className="space-y-1">
            {teamSpaces.map((space) => {
              if (!space.space_id) {
                return null;
              }

              const isActive = space.space_id === currentSpaceId;
              const isPersonal = space.space_id === PERSONAL_TEAM_SPACE_ID;

              return (
                <button
                  key={space.space_id}
                  type="button"
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-left transition-colors',
                    isActive ? 'bg-system-white08' : 'hover:bg-system-white04',
                  )}
                  onClick={() => handleSpaceSwitch(space)}
                >
                  <div className="flex size-6 shrink-0 items-center justify-center text-white">
                    <TeamAvatar
                      spaceId={space.space_id}
                      teamName={space.team_name}
                      className="size-5 overflow-hidden rounded-6 font-medium text-sm"
                    />
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col truncate font-normal text-sm text-white leading-6">
                    {isPersonal
                      ? t('switchToPersonalWorkspace')
                      : t('switchToWorkspace', { name: space.team_name || '' })}
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <div className="h-4 w-[1px] bg-system-white24" />
    </>
  );
};
